import { h } from '@omni-torn/ui';
import {
  ALERT_RULES_LIST,
  ALERT_RULES_MAX,
  alertRulesDoc,
  alertsFor,
  cleanRule,
  COUNTRY_NAMES,
  deliveryVia,
  describeRule,
  ALERTS_POOLED_TERMS,
  pooledHeld,
  POOLED_HOLD_TEXT,
  MARKET_FAVORITES_LIST,
  newRuleId,
  readAlertRules,
  readFavorites,
  searchItems,
  type AlertFired,
  type AlertKind,
  type AlertRule,
  type AlertsAnswer,
  type AlertVia,
} from '@omni-torn/shared';
import { FLIGHT_TYPES } from '@omni-torn/shared/abroad';
import type { StocksAnswer } from '@omni-torn/shared/stocks';
import type { RefItem } from '@omni-torn/shared/torn-ref';
import { server, session } from '../session';
import { ref } from '../torn-lists';
import { ago, box, ext, loading, problem, table, tct } from '../ui';

/**
 * Alerts (website roadmap S34 — Torn Intel Platinum's price alerts and phone app, weav3r's favorites), under Omni-Torn
 * beside Account:
 *  - Your rules: stock price targets and % moves, your favorites' target prices (Market → Deals), stock abroad back in
 *    stock or "leave now", your bank investment ending and your landing. They're your own list, kept with your lists
 *    (`/api/me/lists`, ALERT_RULES_LIST — synced with the extension), nothing new stored.
 *  - The server checks them every minute on what it already has (apps/api/src/alerts.ts) and sends each where you chose:
 *    the extension (a notification), Discord (a DM from the Omni-Torn bot to your own linked Discord, a few an hour at
 *    most) or this website while it's open (a browser notification, once you allow it).
 *  - The site can be installed (a web app manifest and a service worker that keeps only the site's shell).
 */

const SITE_KEY = 'omni-torn.siteAlerts';
const SEEN_KEY = 'omni-torn.siteAlertsSeen';

// ---------------------------------------------------------------- installing the site

interface InstallPrompt extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: string }>;
}
let installPrompt: InstallPrompt | null = null;
addEventListener('beforeinstallprompt', (e) => {
  e.preventDefault();
  installPrompt = e as InstallPrompt;
});

const installed = (): boolean => matchMedia?.('(display-mode: standalone)').matches === true || (navigator as Navigator & { standalone?: boolean }).standalone === true;

/** The service worker that makes the site installable (shell only — apps/web/src/sw.js). */
export function registerServiceWorker(): void {
  if (!('serviceWorker' in navigator)) return;
  addEventListener('load', () => void navigator.serviceWorker.register('/sw.js', { scope: '/' }).catch(() => {}), { once: true });
}

function installCard(): HTMLElement {
  const out = h('div', { 'data-install': '' });
  const draw = () => {
    if (installed()) return out.replaceChildren(h('p', {}, '✓ You’re using Omni-Torn as an installed app.'));
    const how = h('p', { class: 'muted small' }, 'On a phone: your browser’s menu → “Install app” or “Add to Home Screen”. On a computer: the install icon in the address bar (Chrome, Edge). Firefox on a computer can’t install sites.');
    if (!installPrompt) return out.replaceChildren(h('p', {}, 'Install Omni-Torn to open it like an app, from your home screen or desktop.'), how);
    out.replaceChildren(
      h('p', {}, 'Install Omni-Torn to open it like an app, from your home screen or desktop.'),
      h('button', {
        type: 'button',
        'data-install-button': '',
        onclick: () => {
          const p = installPrompt;
          installPrompt = null;
          void p?.prompt().then(() => p.userChoice).finally(draw);
        },
      }, 'Install Omni-Torn'),
    );
  };
  draw();
  return box('Install the site', out, { id: 'install' });
}

// ---------------------------------------------------------------- this website's notifications

const siteOn = (): boolean => {
  try {
    return localStorage.getItem(SITE_KEY) === 'on';
  } catch {
    return false;
  }
};
const setSite = (on: boolean): void => {
  try {
    if (on) localStorage.setItem(SITE_KEY, 'on');
    else localStorage.removeItem(SITE_KEY);
  } catch {
    /* this visit only */
  }
};

async function showNote(a: AlertFired): Promise<void> {
  const opts = { body: a.text, tag: `ot-${a.id}`, icon: '/icons/icon-192.png', data: { url: a.url } };
  const reg = await navigator.serviceWorker?.getRegistration().catch(() => undefined);
  if (reg) return reg.showNotification(a.title, opts);
  const n = new Notification(a.title, opts);
  n.onclick = () => {
    if (/^https:\/\/www\.torn\.com\//.test(a.url)) open(a.url, '_blank', 'noopener');
  };
}

let watching: ReturnType<typeof setInterval> | null = null;

/**
 * While the site is open and you allowed it: what the server's checks found for rules that go to "this website", as
 * browser notifications (asked once a minute). Nothing is asked when it's off or you're signed out.
 */
export function startSiteAlerts(): void {
  if (watching || typeof Notification === 'undefined') return;
  const check = async () => {
    if (!session.get() || !siteOn() || Notification.permission !== 'granted') return;
    let since = Date.now() - 10 * 60_000;
    try {
      since = Math.max(since, Number(localStorage.getItem(SEEN_KEY)) || 0);
    } catch {
      /* no storage */
    }
    const r = await server<AlertsAnswer>('GET', `/api/me/alerts?since=${Math.floor(since)}`).catch(() => null);
    if (!r) return;
    for (const a of alertsFor(r.alerts, 'site', since).slice(-5)) await showNote(a).catch(() => {});
    const newest = Math.max(since, ...r.alerts.map((a) => a.at));
    try {
      localStorage.setItem(SEEN_KEY, String(newest));
    } catch {
      /* this visit only */
    }
  };
  watching = setInterval(() => void check(), 60_000);
  void check();
}

function siteCard(): HTMLElement {
  const out = h('div', {});
  const draw = () => {
    if (typeof Notification === 'undefined') return out.replaceChildren(h('p', { class: 'muted' }, 'This browser can’t show notifications from a website.'));
    const box1 = h('input', { type: 'checkbox', checked: siteOn() && Notification.permission === 'granted', 'data-site-alerts': '' }) as HTMLInputElement;
    box1.addEventListener('change', () => {
      if (!box1.checked) {
        setSite(false);
        return draw();
      }
      // Asked only on your click (browsers allow nothing else).
      void Notification.requestPermission().then((p) => {
        setSite(p === 'granted');
        draw();
        if (p === 'granted') startSiteAlerts();
      });
    });
    out.replaceChildren(
      h('label', { class: 'check' }, box1, ' Show my alerts as notifications while this website is open'),
      h('p', { class: 'muted small' }, Notification.permission === 'denied' ? 'Your browser blocks notifications from this site — allow them in the site’s settings (the padlock in the address bar).' : 'Only alerts you set to go to “This website”. Nothing is stored for it on the server: with the site closed, use the extension or Discord.'),
    );
  };
  draw();
  return box('This website', out, { id: 'alerts-site' });
}

// ---------------------------------------------------------------- the page

interface ListsDocAnswer {
  lists: Record<string, unknown> | null;
  updatedAt: number | null;
}

const KIND_LABELS: Record<AlertKind, string> = {
  stock: 'Stock price',
  stockMove: 'Stock move (%)',
  favorites: 'Favorites’ target prices',
  abroad: 'Stock abroad',
  bank: 'Bank investment ends',
  travel: 'Landing',
};
const VIA_LABELS: Record<keyof AlertVia, string> = { ext: 'Extension', discord: 'Discord', site: 'This website' };

export async function renderAlerts(view: HTMLElement): Promise<void> {
  const rulesOut = h('div', {}, loading());
  const addOut = h('div', {});
  const feedOut = h('div', {}, loading());
  const pageNote = h('div', { 'data-page-alerts': '' });
  view.replaceChildren(
    h('div', { class: 'page-head' }, h('h1', {}, 'Alerts'), h('p', { class: 'muted' }, 'Stock prices and moves, your favorites’ target prices, stock abroad and when to leave, your bank investment and your landing — sent to the extension, to Discord, or to this website.')),
    box('Your alerts', rulesOut, { id: 'alert-rules' }),
    box('Add an alert', addOut, { id: 'alert-add' }),
    box('Recent alerts', [pageNote, feedOut], { id: 'alert-feed' }),
    siteCard(),
    installCard(),
    box('How alerts work', [
      h('p', {}, `Omni-Torn’s server checks your alerts every minute against what it already has: stock prices (read every 15 minutes), the market listings Omni-Torn users saw, stock abroad Omni-Torn users saw, and — with background tracking on — your bank investment (read hourly with a Limited key, with history kept) and your flight (from your faction share, so only in a faction). It asks Torn nothing more for them. Alerts from what Omni-Torn users saw (stock abroad, your favorites’ prices) go wherever you choose from terms version ${ALERTS_POOLED_TERMS}; before you agree to it, only to this page while it’s open.`),
      h('p', {}, 'Your alerts are kept with your lists and notes (Account → the same settings, lists and notes on all your devices), and go when you delete your data. Discord: a DM from the Omni-Torn bot to the Discord account linked to your Torn account (by Torn or with your key), at most a few an hour. The extension shows them as notifications even with Torn closed.'),
    ], { id: 'alerts-how' }),
  );

  // What the form needs: stocks, items, shops abroad (each may be missing — the rest still works).
  const [stocks, items, abroad, first] = await Promise.all([
    server<StocksAnswer>('GET', '/api/stocks').catch(() => null),
    ref('items').then((a) => a.data as RefItem[]).catch(() => [] as RefItem[]),
    server<{ countries: Record<string, { items: Array<{ id: number; name: string | null }> }> }>('GET', '/api/travel/forecast').catch(() => null),
    server<AlertsAnswer>('GET', '/api/me/alerts?since=0').catch(() => null),
  ]);
  /** On terms 17 (owner's call 2026-11-02) stock abroad and favorites go to the background channels like the rest. */
  const pooledTerms = first?.pooledInBackground === true ? ALERTS_POOLED_TERMS : 0;
  const held = (kind: AlertKind | 'test') => pooledHeld(kind, pooledTerms);
  const stockName = (id: number) => stocks?.stocks.find((s) => s.id === id)?.acronym ?? null;
  const itemName = (id: number) => items.find((i) => i.id === id)?.name ?? null;
  const names = { stock: stockName, item: itemName };

  let doc: ListsDocAnswer = { lists: null, updatedAt: null };
  let rules: AlertRule[] = [];
  const read = async (): Promise<void> => {
    doc = await server<ListsDocAnswer>('GET', '/api/me/lists');
    rules = readAlertRules(doc.lists);
  };
  /** Change your rules in your lists (the server's copy fresh; sent again once if another device wrote between). */
  const save = async (change: (r: AlertRule[]) => AlertRule[]): Promise<void> => {
    for (let i = 0; i < 2; i++) {
      await read();
      const next = change(rules);
      const r = await server<{ applied: boolean; lists: Record<string, unknown> }>('PUT', '/api/me/lists', { lists: { ...(doc.lists ?? {}), [ALERT_RULES_LIST]: alertRulesDoc(next) }, updatedAt: Date.now(), device: 'Website', base: doc.updatedAt ?? null });
      if (r.applied) {
        rules = readAlertRules(r.lists);
        doc = { lists: r.lists, updatedAt: null };
        return;
      }
    }
    throw new Error('Another device changed your lists just now — try again.');
  };

  const viaText = (v: AlertVia) => (Object.keys(VIA_LABELS) as Array<keyof AlertVia>).filter((k) => v[k]).map((k) => VIA_LABELS[k]).join(', ') || 'Only this page';
  /** Pooled kinds (stock abroad, favorites) below terms 17: this page only, while it's open. */
  const goesTo = (kind: AlertKind | 'test', v: AlertVia) => (held(kind) ? h('span', { 'data-page-only': '' }, 'This page while it’s open', h('div', { class: 'muted small' }, `To send it elsewhere too, agree to terms version ${ALERTS_POOLED_TERMS}.`)) : viaText(deliveryVia(kind, v, pooledTerms)));
  const drawRules = (msg = '') => {
    const note = h('p', { class: 'muted small', 'data-rules-note': '' }, msg);
    rulesOut.replaceChildren(
      table(
        rules,
        [
          { label: 'Alert', get: (r) => h('span', { 'data-rule': r.id }, describeRule(r, names)) },
          { label: 'Goes to', get: (r) => goesTo(r.kind, r.via) },
          {
            label: 'On',
            get: (r) => {
              const c = h('input', { type: 'checkbox', checked: r.on, 'aria-label': 'On', 'data-rule-on': r.id }) as HTMLInputElement;
              c.addEventListener('change', () => void save((list) => list.map((x) => (x.id === r.id ? { ...x, on: c.checked, at: Date.now() } : x))).then(() => drawRules('Saved.'), (e) => drawRules(e instanceof Error ? e.message : 'Couldn’t save.')));
              return c;
            },
          },
          { label: '', get: (r) => h('button', { type: 'button', class: 'btn-small', 'data-rule-remove': r.id, onclick: () => void save((list) => list.filter((x) => x.id !== r.id)).then(() => drawRules('Removed.'), (e) => drawRules(e instanceof Error ? e.message : 'Couldn’t save.')) }, 'Remove') },
        ],
        { empty: 'No alerts yet — add one below.' },
      ),
      note,
    );
  };

  // ---- the "add" form
  const kindSel = h('select', { 'data-alert': 'kind', 'aria-label': 'Kind' }, ...(Object.keys(KIND_LABELS) as AlertKind[]).map((k) => h('option', { value: k }, KIND_LABELS[k]))) as HTMLSelectElement;
  const fields = h('div', { class: 'toolbar alert-fields' });
  const viaBoxes = (Object.keys(VIA_LABELS) as Array<keyof AlertVia>).map((k) => h('input', { type: 'checkbox', checked: k === 'ext', 'data-via': k }) as HTMLInputElement);
  const msg = h('span', { class: 'muted small', 'data-add-note': '' });
  const holdNote = h('p', { class: 'muted small', 'data-pooled-hold': '', hidden: true }, POOLED_HOLD_TEXT);
  /**
   * A pooled kind below terms 17: the note says it shows on this page while it's open for now. The places the player ticks
   * are saved as chosen (the server leaves them out until terms 17 — `deliveryVia` at fire time), so agreeing to 17 later
   * needs no edit.
   */
  const drawVia = () => {
    holdNote.hidden = !held(kindSel.value as AlertKind);
  };
  const input = (name: string, attrs: Record<string, string> = {}) => h('input', { 'data-field': name, 'aria-label': name, ...attrs }) as HTMLInputElement;
  const select = (name: string, opts: Array<[string, string]>) => h('select', { 'data-field': name, 'aria-label': name }, ...opts.map(([v, t]) => h('option', { value: v }, t))) as HTMLSelectElement;
  const stockOpts: Array<[string, string]> = (stocks?.stocks ?? []).map((s) => [String(s.id), `${s.acronym} — ${s.name} ($${s.price.toFixed(2)})`]);
  const countryOpts: Array<[string, string]> = Object.entries(COUNTRY_NAMES).map(([c, n]) => [c, n]);
  const field = (name: string): string => (fields.querySelector(`[data-field="${name}"]`) as HTMLInputElement | HTMLSelectElement | null)?.value ?? '';
  const label = (text: string, el: HTMLElement) => h('label', {}, `${text} `, el);
  const drawFields = () => {
    const k = kindSel.value as AlertKind;
    const noStocks = h('span', { class: 'muted small' }, 'The server has no stock prices yet.');
    switch (k) {
      case 'stock':
        fields.replaceChildren(...(stockOpts.length ? [label('Stock', select('stock', stockOpts)), label('At or above $', input('above', { type: 'number', min: '0', step: '0.01', class: 'num-input' })), label('At or below $', input('below', { type: 'number', min: '0', step: '0.01', class: 'num-input' }))] : [noStocks]));
        break;
      case 'stockMove':
        fields.replaceChildren(
          ...(stockOpts.length
            ? [
                label('Stock', select('stock', stockOpts)),
                label('', select('dir', [['any', 'Up or down'], ['up', 'Up'], ['down', 'Down']])),
                label('at least', input('pct', { type: 'number', min: '0.1', step: '0.1', value: '5', class: 'num-input' })),
                h('span', {}, '% over'),
                select('hours', [['1', 'an hour'], ['6', '6 hours'], ['24', 'a day'], ['168', 'a week']]),
              ]
            : [noStocks]),
        );
        break;
      case 'favorites': {
        const favs = readFavorites(doc.lists).filter((f) => f.target > 0);
        fields.replaceChildren(h('span', {}, `Uses the target prices of your favorites (${favs.length} with a target) — set them in `, h('a', { href: '/market/deals', 'data-link': true }, 'Market → Deals'), '.'));
        break;
      }
      case 'abroad': {
        const itemsIn = input('item', { type: 'search', list: 'alert-abroad-items', placeholder: 'Item', maxlength: '40' });
        const seen = new Map<number, string>();
        for (const c of Object.values(abroad?.countries ?? {})) for (const it of c.items) seen.set(it.id, it.name ?? itemName(it.id) ?? `#${it.id}`);
        fields.replaceChildren(
          label('Country', select('country', countryOpts)),
          label('Item', itemsIn),
          h('datalist', { id: 'alert-abroad-items' }, ...[...seen.values()].sort().map((n) => h('option', { value: n }))),
          select('mode', [['stock', 'Back in stock'], ['leave', 'Leave now (lands with stock)']]),
          label('at least', input('want', { type: 'number', min: '1', step: '1', value: '1', class: 'num-input' })),
          label('Flight', select('flight', FLIGHT_TYPES.map((f) => [f, f === 'private' ? 'private island' : f]))),
        );
        break;
      }
      case 'bank':
        fields.replaceChildren(label('Minutes before it ends', input('before', { type: 'number', min: '0', max: '1440', step: '1', value: '0', class: 'num-input' })), h('span', { class: 'muted small' }, 'Needs background tracking with history kept and a Limited key (read hourly).'));
        break;
      case 'travel':
        fields.replaceChildren(label('Minutes before you land', input('before', { type: 'number', min: '0', max: '120', step: '1', value: '0', class: 'num-input' })), h('span', { class: 'muted small' }, 'From your faction share (background tracking, or the extension): only while you’re in a faction.'));
        break;
    }
  };
  kindSel.addEventListener('change', () => (drawFields(), drawVia()));
  const build = (): AlertRule | null => {
    const k = kindSel.value as AlertKind;
    // As chosen — never cleared here: below terms 17 the server holds a pooled kind to this page when it goes off.
    const via: AlertVia = { ext: viaBoxes[0]!.checked, discord: viaBoxes[1]!.checked, site: viaBoxes[2]!.checked };
    const raw: Record<string, unknown> = { kind: k, on: true, via, at: Date.now() };
    if (k === 'stock' || k === 'stockMove') raw.stockId = Number(field('stock'));
    if (k === 'stock') Object.assign(raw, { above: field('above') || null, below: field('below') || null });
    if (k === 'stockMove') Object.assign(raw, { pct: field('pct'), hours: field('hours'), dir: field('dir') });
    if (k === 'abroad') {
      const name = field('item');
      const it = searchItems(items, name, 1)[0];
      Object.assign(raw, { country: field('country'), itemId: it?.id ?? 0, mode: field('mode'), want: field('want'), flight: field('flight') });
    }
    if (k === 'bank' || k === 'travel') raw.before = field('before');
    return cleanRule(newRuleId(Date.now()), raw);
  };
  addOut.replaceChildren(
    h(
      'form',
      {
        class: 'alert-add-form',
        'data-alert': 'add',
        onsubmit: (e: Event) => {
          e.preventDefault();
          const r = build();
          if (!r) return void (msg.textContent = kindSel.value === 'abroad' ? 'Pick a country and an item by its name.' : kindSel.value === 'stock' ? 'Give a price to be at or above, or at or below.' : 'Something’s missing.');
          if (rules.length >= ALERT_RULES_MAX) return void (msg.textContent = `You have ${ALERT_RULES_MAX} alerts — remove one first.`);
          msg.textContent = 'Saving…';
          save((list) => [...list, r])
            .then(() => {
              msg.textContent = `Added: ${describeRule(r, names)}.`;
              drawRules();
            })
            .catch((err) => (msg.textContent = err instanceof Error ? err.message : 'Couldn’t save.'));
        },
      },
      h('div', { class: 'toolbar' }, label('Alert me about', kindSel)),
      fields,
      h('div', { class: 'toolbar' }, h('span', {}, 'Send it to:'), ...viaBoxes.map((b, i) => h('label', { class: 'check' }, b, ` ${Object.values(VIA_LABELS)[i]}`))),
      holdNote,
      h('div', { class: 'toolbar' }, h('button', { type: 'submit' }, 'Add alert'), msg),
    ),
  );

  // ---- what went off
  const drawFeed = async () => {
    let a: AlertsAnswer;
    try {
      a = await server<AlertsAnswer>('GET', '/api/me/alerts?since=0');
    } catch (e) {
      return feedOut.replaceChildren(problem(e));
    }
    const testMsg = h('span', { class: 'muted small', 'data-test-note': '' });
    const status = [
      a.stocksAt ? `Stock prices from ${ago(a.stocksAt)}.` : 'No stock prices on the server yet.',
      a.bankUntil ? `Your bank investment ends ${tct(a.bankUntil)}.` : 'Your bank investment: not known (needs background tracking, history and a Limited key).',
      a.travel ? `Your flight: ${a.travel.destination}, landing ${tct(a.travel.arrivalAt)}.` : 'Not flying (or your flight isn’t shared).',
    ];
    feedOut.replaceChildren(
      table(
        a.alerts,
        [
          { label: 'When', get: (x) => h('span', { class: 'muted small' }, ago(x.at)), sort: (x) => x.at },
          { label: 'Alert', get: (x) => h('span', { 'data-fired': x.kind }, h('strong', {}, x.title), ` — ${x.text}`) },
          { label: 'Sent to', get: (x) => (x.pageOnly ? 'This page' : `${viaText(x.via)}${x.discord && x.discord !== 'sent' ? ` (Discord: ${x.discord === 'not-linked' ? 'no linked Discord' : x.discord === 'limit' ? 'hourly limit reached' : x.discord === 'terms' ? 'needs terms version 16' : x.discord === 'closed' ? 'your DMs are closed to the bot — paused a day' : 'couldn’t DM you'})` : ''}`) },
          { label: '', get: (x) => (x.url ? ext(x.url, 'Open') : '') },
        ],
        { empty: 'Nothing has gone off in the last day.' },
      ),
      h('p', { class: 'muted small', 'data-alert-status': '' }, status.join(' ')),
      h(
        'div',
        { class: 'toolbar' },
        h('button', {
          type: 'button',
          class: 'btn-small',
          'data-alert-test': '',
          onclick: () => {
            testMsg.textContent = 'Sending…';
            const via: AlertVia = { ext: viaBoxes[0]!.checked, discord: viaBoxes[1]!.checked, site: viaBoxes[2]!.checked };
            server<{ alert: AlertFired }>('POST', '/api/me/alerts/test', { via })
              .then((r) => {
                testMsg.textContent = r.alert.discord && r.alert.discord !== 'sent' ? 'Sent — but Discord couldn’t DM you (is your Discord linked, and DMs open?).' : 'Sent.';
                void drawFeed();
              })
              .catch((e) => (testMsg.textContent = e instanceof Error ? e.message : 'Couldn’t send.'));
          },
        }, 'Send a test alert'),
        h('span', { class: 'muted small' }, ' (to the places ticked under “Add an alert”) '),
        testMsg,
      ),
    );
  };

  try {
    await read();
  } catch (e) {
    rulesOut.replaceChildren(problem(e));
    return;
  }
  drawRules();
  drawFields();
  drawVia();
  await drawFeed();
  // While this page is open and on screen: what went off (stock abroad and favorites show only here) — asked once a
  // minute, stopped when you leave the page.
  let seen = Date.now();
  const timer = setInterval(() => {
    if (!feedOut.isConnected) return clearInterval(timer);
    if (document.visibilityState !== 'visible') return;
    void server<AlertsAnswer>('GET', `/api/me/alerts?since=${Math.floor(seen)}`)
      .then((r) => {
        if (!r.alerts.length) return;
        seen = Math.max(seen, ...r.alerts.map((a) => a.at));
        const page = r.alerts.filter((a) => a.pageOnly);
        if (page.length) pageNote.replaceChildren(h('div', { class: 'notice', role: 'status', 'data-page-alert': '' }, ...page.slice(0, 5).map((a) => h('div', {}, h('strong', {}, a.title), ` — ${a.text} `, a.url ? ext(a.url, 'Open') : ''))));
        void drawFeed();
      })
      .catch(() => {});
  }, 60_000);
}
